/**
 * TOPBAR — Header del nuovo design (port dal mockup): logo FeelHippo a sinistra,
 * ricerca centrale (entity-search reale) al centro, menu Lingua e Utente a
 * destra. Le voci/link dei menu restano quelle reali (Impostazioni → /settings/
 * user, Logout, cambio lingua it/en).
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { logout } from '../auth/authSlice';
import { useDebounce } from '@crm/shared';
import { resolveEntityLabel, extractEntityIdFromPayload, entityTypeLabelPlural, getEntityType } from '../entityRegistry/entityRegistry';
import { useToast } from '../components/useToast';
import { useEnabledEntityTypes } from '../plugin/useEnabledEntityTypes';
import { useDisabledCoreItems, useDisabledEntityTypes } from '../plugin/useCoreItems';
import { isPathDisabled } from '../plugin/coreItems';
import { useCurrentGroupId } from '../auth/authSelectors';
import { useLanguage, useProfileLanguageSync } from '../components/useLanguage';
import { resolvePortraitURL, resolveLiferayImageURL } from '../api/portrait';
import { SearchGlyph, ChevronDownGlyph, SettingsGlyph, LogoutGlyph } from '../components/icons';

interface SearchOption {
  id: number;
  label: string;
  path: string;
  typeName: string;
  typeLabel: string;
}

type OpenMenu = 'user' | null;

function initialsOf(name?: string): string {
  if (!name) return '';
  return name.trim().split(/\s+/).slice(0, 2).map((n) => n[0]?.toUpperCase() ?? '').join('');
}

export function TopBar() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const { language, changeLanguage } = useLanguage();
  // Il profilo segue la lingua dell'app (login e toggle qui sotto): vedi l'hook.
  useProfileLanguageSync();
  const user = useAppSelector((state) => state.auth.user);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<SearchOption[]>([]);
  const [loading, setLoading] = useState(false);
  const debouncedQuery = useDebounce(inputValue, 300);
  const enabledEntityTypes = useEnabledEntityTypes();
  const disabledEntityTypes = useDisabledEntityTypes();
  const disabledCoreItems = useDisabledCoreItems();
  const groupId = useCurrentGroupId();
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  // Portrait dell'utente (relativo → assoluto). Se assente/errore, si usano le iniziali.
  const avatarSrc = resolvePortraitURL(user?.portraitURL);

  // Etichetta scorciatoia coerente col sistema.
  const isMac =
    typeof navigator !== 'undefined' &&
    /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent);
  const searchShortcut = isMac ? '⌘K' : 'Ctrl K';

  // Logo header: quello aziendale custom se impostato, altrimenti il default
  // FeelHippo. companyLogoURL è relativo -> reso assoluto verso l'istanza Liferay.
  const logoSrc = user?.companyLogoURL
    ? resolveLiferayImageURL(user.companyLogoURL) ?? '/logo.svg'
    : '/logo.svg';

  // Titolo della scheda del browser: il nome dell'istanza, come accanto al logo. Al
  // logout (TopBar smontata) torna quello di index.html.
  const companyName = user?.companyName;
  useEffect(() => {
    const original = document.title;
    if (companyName) document.title = companyName;
    return () => { document.title = original; };
  }, [companyName]);

  // Cmd/Ctrl+K → focus sulla ricerca (l'input è gestito da Autocomplete: lo
  // raggiungo dal DOM per non entrare in conflitto col suo ref interno).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        document.querySelector<HTMLInputElement>('.searchbox input')?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Chiusura dei menu al click fuori da qualsiasi .hmenu-wrap.
  useEffect(() => {
    if (!openMenu) return;
    const onDown = (e: MouseEvent) => {
      if (!(e.target as Element).closest('.hmenu-wrap')) setOpenMenu(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [openMenu]);

  // Ricerca entità (core + plugin abilitati), soglia 3 caratteri.
  useEffect(() => {
    const query = debouncedQuery;
    if (query.length < 3) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const entityTypes = enabledEntityTypes.filter((et) => et.search);
    (async () => {
      try {
        const settled = await Promise.allSettled(
          entityTypes.map(async (et) => {
            const page = await et.search!(query, groupId, { pageSize: 5 });
            return Promise.all(
              page.items.map(async (entity) => {
                const id = extractEntityIdFromPayload(entity, et.typeName);
                const label = await resolveEntityLabel(et.typeName, id);
                const path = et.detailPath(id);
                return { id, label, path, typeName: et.typeName, typeLabel: entityTypeLabelPlural(et, t, et.typeName) };
              }),
            );
          }),
        );
        if (cancelled) return;
        setOptions(
          settled
            .filter((s): s is PromiseFulfilledResult<SearchOption[]> => s.status === 'fulfilled')
            .flatMap((s) => s.value),
        );
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [debouncedQuery, enabledEntityTypes, groupId, t]);

  // FILO-26: i tipi senza pagina propria (le attività) dichiarano `resolveDetailPath`, che legge
  // le relazioni e porta alla scheda del record collegato. `undefined` = nessun record collegato:
  // si avvisa invece di navigare verso un 404.
  async function openResult(option: SearchOption) {
    setInputValue('');
    setOptions([]);
    const resolve = getEntityType(option.typeName)?.resolveDetailPath;
    try {
      const path = resolve ? await resolve(option.id, groupId, disabledEntityTypes) : option.path;
      // Un posto dentro una voce spenta (FILO-53: una mail con l'Inbox spenta) vale come nessun
      // posto: il router riporterebbe alla home senza dire perché.
      if (path && !isPathDisabled(path, disabledCoreItems)) navigate(path);
      else toast({ severity: 'warning', message: t('topbar.searchNotLinked', { label: option.label }) });
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }

  return (
    <header className="topbar">
      <div className="topbar-side">
        <Link to="/tasks" className="brand-logo" aria-label={t('brand.name')}>
          <img src={logoSrc} alt={t('brand.name')} className="topbar-logo-img" />
          {user?.companyName && <span className="topbar-company">{user.companyName}</span>}
        </Link>
      </div>

      <div className="searchbox">
        <SearchGlyph width={15} height={15} />
        <Autocomplete
          freeSolo
          forcePopupIcon={false}
          options={options}
          groupBy={(option) => (typeof option === 'string' ? '' : option.typeLabel)}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
          filterOptions={(x) => x}
          inputValue={inputValue}
          onInputChange={(_e, value) => setInputValue(value)}
          onChange={(_e, value) => {
            if (value && typeof value !== 'string') void openResult(value);
          }}
          loading={loading}
          loadingText={t('common.search')}
          noOptionsText={inputValue.length >= 2 ? t('common.noResults') : t('common.searchType')}
          renderInput={(params) => (
            <TextField {...params} placeholder={t('common.search')} size="small" />
          )}
        />
        <kbd aria-hidden="true">{searchShortcut}</kbd>
      </div>

      <div className="topbar-side topbar-right">
        {/* Lingua: switch diretto it↔en. */}
        <button
          type="button"
          className="hbtn lang-btn"
          onClick={() => changeLanguage(language === 'it' ? 'en' : 'it')}
          aria-label={`${t('userSettings.profile.language')}: ${language.toUpperCase()}`}
        >
          <span className="lang-code">{language.toUpperCase()}</span>
        </button>

        {/* Utente: Impostazioni (→ /settings/user) + Logout, come prima. */}
        <div className={`hmenu-wrap${openMenu === 'user' ? ' open' : ''}`} data-menu="user">
          <button
            type="button"
            className="hbtn user-btn"
            onClick={() => setOpenMenu((m) => (m === 'user' ? null : 'user'))}
            aria-label={t('topbar.userMenu')}
          >
            {avatarSrc
              ? <img className="user-av" src={avatarSrc} alt={user?.fullName ?? ''} />
              : <span className="user-av">{initialsOf(user?.fullName)}</span>}
            {user && <span className="user-name">{user.fullName}</span>}
            <ChevronDownGlyph className="chev" />
          </button>
          <div className="hmenu">
            <button onClick={() => { setOpenMenu(null); navigate('/settings/user'); }}>
              <SettingsGlyph />
              {t('topbar.userSettings')}
            </button>
            <hr />
            <button className="danger" onClick={() => { setOpenMenu(null); dispatch(logout()); }}>
              <LogoutGlyph />
              {t('common.logout')}
            </button>
            {/* FILO-64: arriva nel DTO dell'utente; con un backend che non la manda il box non c'è. */}
            {user?.platformVersion && (
              <div className="hmenu-version">
                <div>{t('topbar.platformVersion', { version: user.platformVersion })}</div>
                {user.platformReleaseDate && (
                  <div>{t('topbar.platformReleaseDate', { date: user.platformReleaseDate })}</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
